import { isConnected, getSession, onConnect } from '../../../em-shared-session/src/connection';


/**
 * Defaults to base call provision on
 */
const defaultOptions = {
    // RPC to call
    rpcName: '',

    // parameters parsing and defaults go here, should not throw errors
    parseParams: () => {},

    // response validator - should return true or false, when response is invalid
    validateResponse: data => !!data && typeof data.kwargs !== 'undefined',

    // check whether a response qualifies as a request error (invalid parameters, etc.)
    // occurrs after validation failure - if it returns false, then the API is broken and an error is thrown
    isErrorResponse: data => data && data.kwargs && data.kwargs.error,

    // response parser - this is to reformat the data (ex.: kwargs removal)
    parseResponse: data => data.kwargs,

    // when calls fail recoverably, retry after this amount of time (minimum)
    // @TODO: retry_delay_growth and retry_delay_jitter from autobahn reconnect logic
    retryAfter: 3000,
};


/**
 * List Updates Handlers
 */
let handlersIndex = 0;

const onPendingCallsUpdateHandlers = {};
const pendingCallsList = {};

// Logic to attach a handler
function addHandler(handlers = {}, fn = () => {}) {
    const handlerIndex = ++handlersIndex;
    const targetHandlers = handlers;
    targetHandlers[handlerIndex] = fn;
    return () => {
        delete targetHandlers[handlerIndex];
    };
}

// Logic to call all attached handlers
function callHandlers(handlers = {}, params = []) {
    for (const handlerIndex of Object.keys(handlers)) {
        if (typeof handlers[handlerIndex] === 'function') {
            handlers[handlerIndex](...params);
        }
    }
}

// Retrieve coalesced pendingCalls list
function getPendingCallsList() {
    const r = [];
    for (const methodName of Object.keys(pendingCallsList)) {
        for (const callId of Object.keys(pendingCallsList[methodName])) {
            r.push(pendingCallsList[methodName][callId]);
        }
    }
    return r;
}


/**
 * onPendingCallsUpdate - attaches handlers - bound to api.onPendingCallsUpdate
 */
export function onPendingCallsUpdate(fn = () => {}) {
    return addHandler(onPendingCallsUpdateHandlers, fn);
}

/**
 * provideCall - exported to API core
 */
let automaticClientRequestId = 0;
export default function provideCall(callMethodName = '', cancelCallMethodName = '', preferredOptions = {}) {
    // Apply defaults, validate options
    const options = Object.assign({}, defaultOptions, preferredOptions);
    if (!options.rpcName || typeof options.rpcName !== 'string' || !options.rpcName.length) {
        throw new Error(`Invalid RPC name supplied to provideCall ${options.rpcName}`);
    }

    const { rpcName, parseParams, isErrorResponse, validateResponse, parseResponse, retryAfter } = options;

    /**
     * Process all pending calls
     */
    const pendingCalls = {};
    function processPendingCalls() {
        const session = getSession();
        if (!session) {
            return false;
        }

        const now = Date.now();

        for (const callId of Object.keys(pendingCalls)) {
            const pendingCall = pendingCalls[callId];
            const { params, onCompletion, onSuccess, onError, isRequested, sessionId, nextRetry } = pendingCall;

            if ((!isRequested || sessionId !== session.id) && (!nextRetry || nextRetry <= now)) {
                const callSessionId = session.id;

                /**
                 * Executing the actual call, and it will only resolve/reject if:
                 * - call was not canceled
                 * - call is not being resolved while session has been invalidated
                 * - session id recorded when call was performed matches currently live session id
                 * - another call has not been attempted
                 * - the current call is the same as the one in the pending calls list
                 *
                 * According to tests, this is well within bounds:
                 * http://stackoverflow.com/questions/20068467/do-never-resolved-promises-cause-memory-leak
                 */
                session.call(rpcName, [], params||{}, {}).then((response) => {
                    // resolving the call
                    const localSession = getSession();
                    if (
                        pendingCalls[callId] && localSession && callSessionId === pendingCalls[callId].sessionId &&
                        callSessionId === localSession.id && pendingCall === pendingCalls[callId]
                    ) {
                        delete pendingCalls[callId];

                        pendingCallsList[callMethodName] = Object.assign({}, pendingCalls);
                        callHandlers(onPendingCallsUpdateHandlers, [getPendingCallsList()]);
                        // Validation succeeds - call is successful, response is good
                        if (validateResponse(response)) {
                            onSuccess(parseResponse(response));
                            onCompletion();
                        // Validation fails, but it is an error, call the error handler
                        // @TODO: APIFIX: {"error": 'INVALID_WHATEVER', "message": "Invalid Whatever"}
                        } else if (isErrorResponse(response)) {
                            onError(response);
                            onCompletion();
                        // In case it doesn't seem to be an error either
                        } else {
                            throw new Error(`Response validation failed for ${rpcName} called with params ${JSON.stringify(params)}.
                                This is very likely to be the result of a breaking change in the API`);
                        }
                    }
                }, (response) => {
                    // rejecting the call
                    const localSession = getSession();
                    if (
                        pendingCalls[callId] && localSession && callSessionId === pendingCalls[callId].sessionId &&
                        callSessionId === localSession.id && pendingCall === pendingCalls[callId]
                    ) {
                        const errorCode = response && response.error || false;
                        switch (errorCode) {
                            // Actual Error From The WAMP Router
                            case 'wamp.error.no_such_procedure':
                            case 'om.rpc.exception':
                                delete pendingCalls[callId];
                                if(rpcName.indexOf('infoTypeUsage')>-1){
                                    break;
                                }
                                pendingCallsList[callMethodName] = Object.assign({}, pendingCalls);
                                callHandlers(onPendingCallsUpdateHandlers, [getPendingCallsList()]);
                                throw new Error(`Procedure ${rpcName} was not found on the server.
                                    This is very likely to be the result of a breaking change in the API`);
                            // Retry the call later
                            case 'wamp.error.no_business_server':
                            case 'wamp.no.backend':
                            default:
                                pendingCalls[callId].nextRetry = Date.now() + retryAfter;
                                pendingCalls[callId].isRequested = false;
                                pendingCalls[callId].sessionId = false;

                                pendingCallsList[callMethodName] = Object.assign({}, pendingCalls);
                                callHandlers(onPendingCallsUpdateHandlers, [getPendingCallsList()]);
                                break;
                        }
                    }
                });

                // mark call as processed under current session
                pendingCall.sessionId = session.id;
                pendingCall.isRequested = true;
                pendingCall.nextRetry = false;

                pendingCallsList[callMethodName] = Object.assign({}, pendingCalls);
                callHandlers(onPendingCallsUpdateHandlers, [getPendingCallsList()]);
            }
        }

        return true;
    }

    // Called to cancel the RPC
    function cancelCall(cancelClientrequestId) {
        if (cancelClientrequestId && pendingCalls[cancelClientrequestId] && {}.hasOwnProperty.call(pendingCalls, cancelClientrequestId)) {
            delete pendingCalls[cancelClientrequestId];

            pendingCallsList[callMethodName] = Object.assign({}, pendingCalls);
            callHandlers(onPendingCallsUpdateHandlers, [getPendingCallsList()]);
        }
    }

    // Called to execute the RPC
    function call({
        clientRequestId, params = {},
        onPending = (() => {}), onCompletion = (() => {}),
        onSuccess = (() => {}),
        onError = (() => {}),
    }) {
        // Call identifier
        const callId = clientRequestId || (++automaticClientRequestId);

        // Existing call with same clientRequestId
        if (clientRequestId && pendingCalls[clientRequestId]) {
            throw new Error(`Existing call found to ${rpcName} using ${clientRequestId};
either serialize calls, cancel the existing call, execute the call under a different clientRequestId, or do not specify a clientRequestId at all`);
        }

        pendingCalls[callId] = {
            rpcName,
            params: parseParams(params),
            isRequested: false,
            sessionId: false,
            nextRetry: false,
            onPending,
            onCompletion,
            onSuccess,
            onError,
            cancelCall: () => cancelCall(callId),
        };

        pendingCallsList[callMethodName] = Object.assign({}, pendingCalls);
        callHandlers(onPendingCallsUpdateHandlers, [getPendingCallsList()]);

        // call onPending()
        onPending();

        // Process if connected
        if (isConnected()) {
            processPendingCalls();
        }
    }

    // Process all pending calls on connect
    onConnect(() => processPendingCalls());
    setInterval(() => processPendingCalls(), defaultOptions.retryAfter);

    // Done
    return { callMethodName, call, cancelCallMethodName, cancelCall };
}



// WEBPACK FOOTER //
// ./src/OMFECore/enhanced-webapi/api-provisioning/provideCall.js